import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Briefcase, MapPin, DollarSign, Clock, CheckCircle, 
  Users, Rocket, GraduationCap, Smartphone, ArrowRight,
  Calendar, ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = '/api';

const AffiliatesPage = () => {
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobData = async () => {
      try {
        const response = await axios.get(`${API}/content/job-listing`);
        if (response.data) {
          setJobData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch job data');
      } finally {
        setLoading(false);
      }
    };
    fetchJobData();
  }, []);

  const handleApplyNow = () => {
    // Navigate to landing page and scroll to get started form
    navigate('/?scrollTo=get-started');
  };

  // Default job data (will be overridden by API data)
  const defaultJobData = {
    title: "Freelance Affiliate Marketing Partner (Remote)",
    jobType: "Freelance / Independent Contractor",
    location: "Remote (Work from anywhere)",
    compensation: "Commission-based (performance-driven with uncapped earning potential)",
    overview: "We are seeking motivated individuals to join our growing digital marketing network as Affiliate Marketing Partners. This is a flexible, freelance opportunity ideal for self-starters looking to generate income online by promoting Affiliate products and services.",
    responsibilities: [
      "Promote Affiliate products and services through online platforms (social media, email, content marketing, etc.). Training Provided.",
      "Drive traffic and generate leads using provided marketing tools and strategies",
      "Build and engage your own audience or network",
      "Track performance and optimize campaigns for better results"
    ],
    whatWeOffer: [
      "Ready-to-use marketing systems, tools and training",
      "Flexible schedule — work at your own pace",
      "No prior experience required (training provided)",
      "Scalable income potential based on performance",
      "Supportive online community and mentorship"
    ],
    requirements: [
      "Basic familiarity with social media or online platforms",
      "Strong communication and willingness to learn",
      "Self-motivated and goal-oriented mindset",
      "Access to a phone or computer with internet"
    ],
    idealCandidate: [
      "Interested in digital marketing, entrepreneurship, or side income opportunities",
      "Looking for flexible, remote work",
      "Comfortable working independently"
    ],
    inPersonEvent: {
      enabled: true,
      text: "Visit our upcoming Masterclass on May 16th 2026 at the Kitchener Library",
      link: "https://events.mnaconsult.com/"
    }
  };

  const job = jobData || defaultJobData;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF4500]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#FF4500] to-[#FF6B35] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="text-xl font-bold text-[#0A0A0A]">MNA Consult</span>
            </a>
            <button
              onClick={handleApplyNow}
              className="bg-[#FF4500] text-white px-6 py-2.5 rounded-lg hover:bg-[#E03E00] transition-all font-semibold text-sm"
              data-testid="header-apply-btn"
            >
              Apply Now
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A0A0A] via-[#1a1a2e] to-[#16213e] text-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF4500] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#0055FF] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Briefcase className="w-4 h-4 text-[#FF4500]" />
              <span className="text-sm font-medium">We're Hiring</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {job.title}
            </h1>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-300 mb-8">
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
                <Clock className="w-4 h-4 text-[#FF4500]" />
                <span>{job.jobType}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-[#0055FF]" />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
                <DollarSign className="w-4 h-4 text-green-400" />
                <span>{job.compensation}</span>
              </div>
            </div>
            
            <button
              onClick={handleApplyNow}
              className="inline-flex items-center gap-2 bg-[#FF4500] text-white px-8 py-4 rounded-xl hover:bg-[#E03E00] transition-all font-semibold text-lg shadow-lg shadow-[#FF4500]/25 hover:scale-105"
              data-testid="hero-apply-btn"
            >
              Apply Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {/* Overview */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF4500]/10 rounded-lg flex items-center justify-center">
                  <Rocket className="w-5 h-5 text-[#FF4500]" />
                </div>
                Overview
              </h2>
              <p className="text-[#525252] leading-relaxed text-lg">
                {job.overview}
              </p>
            </motion.section>

            {/* Key Responsibilities */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0055FF]/10 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#0055FF]" />
                </div>
                Key Responsibilities
              </h2>
              <ul className="space-y-3">
                {job.responsibilities.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-[#525252]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>

            {/* What We Offer */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-green-500" />
                </div>
                What We Offer
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {job.whatWeOffer.map((item, index) => (
                  <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-100 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-[#0A0A0A] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* Requirements */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-purple-500" />
                </div>
                Requirements
              </h2>
              <ul className="space-y-3">
                {job.requirements.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-[#525252]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>

            {/* Ideal Candidate */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FF4500]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#FF4500]" />
                </div>
                Ideal Candidate
              </h2>
              <ul className="space-y-3">
                {job.idealCandidate.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#FF4500] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-[#525252]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>
          </div>

          {/* Right Column - Sticky Apply Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-xl shadow-gray-100/50"
              >
                <h3 className="text-xl font-bold text-[#0A0A0A] mb-4">Ready to Get Started?</h3>
                <p className="text-[#525252] mb-6">
                  Join our growing network of successful affiliate partners and start your journey today.
                </p>
                
                <button
                  onClick={handleApplyNow}
                  className="w-full bg-[#FF4500] text-white py-4 rounded-xl hover:bg-[#E03E00] transition-all font-semibold text-lg flex items-center justify-center gap-2 shadow-lg shadow-[#FF4500]/25 hover:scale-[1.02]"
                  data-testid="sidebar-apply-btn"
                >
                  Apply Now
                  <ArrowRight className="w-5 h-5" />
                </button>
                
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-[#525252] text-center">
                    No experience required. Training provided.
                  </p>
                </div>

                {/* In-Person Event Card */}
                {job.inPersonEvent?.enabled && (
                  <div className="mt-6 bg-gradient-to-br from-[#0055FF]/5 to-[#0055FF]/10 border border-[#0055FF]/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#0055FF] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0A0A0A] text-sm mb-1">In-Person Option</p>
                        <p className="text-[#525252] text-sm mb-3">{job.inPersonEvent.text}</p>
                        <a
                          href={job.inPersonEvent.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#0055FF] hover:text-[#0044CC] font-medium text-sm"
                        >
                          Register Here
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom CTA */}
      <section className="bg-gradient-to-r from-[#FF4500] to-[#FF6B35] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Start Your Journey Today
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Take the first step towards building your own digital marketing business. No experience needed — we'll teach you everything.
            </p>
            <button
              onClick={handleApplyNow}
              className="inline-flex items-center gap-2 bg-white text-[#FF4500] px-8 py-4 rounded-xl hover:bg-gray-100 transition-all font-semibold text-lg shadow-lg hover:scale-105"
              data-testid="bottom-apply-btn"
            >
              Apply Now — It's Free
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            &copy; 2025 MNA Consult. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AffiliatesPage;
